<template>
    <div class="text-editor" id="switcher">
        <div
            class="toolbar"
            :class="{ 'mobile-dropdown-open': showMobileMenu }"
        >
            <button @click.prevent="undo" :title="$vuetify.lang.t('$vuetify.undo')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
                    />
                </svg>
            </button>

            <button @click.prevent="redo" :title="$vuetify.lang.t('$vuetify.redo')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="m15 15 6-6m0 0-6-6m6 6H9a6 6 0 0 0 0 12h3"
                    />
                </svg>
            </button>
            <button
                type="button"
                class="mobile-menu-toggle"
                @click="showMobileMenu = !showMobileMenu"
            >
                ☰
            </button>
            <div
                class="toolbar-items"
                :class="{ hidden: isMobile && !showMobileMenu }"
                @click="handleMobileMenuClick"
            >
                <select
                    v-model="selectedFont"
                    @change="changeFont"
                    class="font-select"
                >
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                </select>

                <input
                    type="color"
                    class="color-picker"
                    v-model="selectedColor"
                    @input="changeTextColor"
                    :title="$vuetify.lang.t('$vuetify.TextColor')"
                />

                <button @click.prevent="bold" :title="$vuetify.lang.t('$vuetify.Bold')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M6 4h8a4 4 0 0 1 0 8H6zm0 8h9a4 4 0 1 1 0 8H6z"
                        />
                    </svg>
                </button>

                <button @click.prevent="italic" :title="$vuetify.lang.t('$vuetify.Italic')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <line x1="19" y1="4" x2="10" y2="4" />
                        <line x1="14" y1="20" x2="5" y2="20" />
                        <line x1="15" y1="4" x2="9" y2="20" />
                    </svg>
                </button>

                <button @click.prevent="createLink" :title="$vuetify.lang.t('$vuetify.Link')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                        />
                    </svg>
                </button>

                <button @click.prevent="alignLeft" :title="$vuetify.lang.t('$vuetify.AlignLeft')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <line x1="3" y1="6" x2="15" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="3" y1="18" x2="17" y2="18" />
                    </svg>
                </button>

                <button @click.prevent="alignCenter" :title="$vuetify.lang.t('$vuetify.AlignCenter')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <line x1="4" y1="6" x2="20" y2="6" />
                        <line x1="6" y1="12" x2="18" y2="12" />
                        <line x1="8" y1="18" x2="16" y2="18" />
                    </svg>
                </button>

                <button @click.prevent="alignRight" :title="$vuetify.lang.t('$vuetify.AlignRight')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <line x1="9" y1="6" x2="21" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="7" y1="18" x2="21" y2="18" />
                    </svg>
                </button>

                <button @click.prevent="insertOrderedList" :title="$vuetify.lang.t('$vuetify.OrderedList')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M8.242 5.992h12m-12 6.003H20.24m-12 5.999h12M4.117 7.495v-3.75H2.99m1.125 3.75H2.99m1.125 0H5.24m-1.92 2.577a1.125 1.125 0 1 1 1.591 1.59l-1.83 1.83h2.16M2.99 15.745h1.125a1.125 1.125 0 0 1 0 2.25H3.74m0-.002h.375a1.125 1.125 0 0 1 0 2.25H2.99"
                        />
                    </svg>
                </button>

                <button
                    @click.prevent="insertUnorderedList"
                    title="Unordered List"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <circle cx="5" cy="6" r="1.5" />
                        <circle cx="5" cy="12" r="1.5" />
                        <circle cx="5" cy="18" r="1.5" />
                        <line x1="9" y1="6" x2="21" y2="6" />
                        <line x1="9" y1="12" x2="21" y2="12" />
                        <line x1="9" y1="18" x2="21" y2="18" />
                    </svg>
                </button>

                <button
                    v-if="!disableImageUpload"
                    @click.prevent="insertImage"
                    :title="$vuetify.lang.t('$vuetify.InsertImage')"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        viewBox="0 0 24 24"
                    >
                        <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                        />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                    </svg>
                </button>

                <button @click.prevent="resetFormat" :title="$vuetify.lang.t('$vuetify.ClearFormatting')">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                    </svg>
                </button>

                <button
                    @click.prevent="setHeading('h1')"
                    :title="$vuetify.lang.t('$vuetify.Heading1')"
                    class="heading-btn"
                >
                    H1
                </button>
                <button
                    @click.prevent="setHeading('h2')"
                    :title="$vuetify.lang.t('$vuetify.Heading2')"
                    class="heading-btn"
                >
                    H2
                </button>
                <button
                    @click.prevent="setHeading('h3')"
                    :title="$vuetify.lang.t('$vuetify.Heading3')"
                    class="heading-btn"
                >
                    H3
                </button>

                <!-- Table Button -->
                <button @click.prevent="showTableDialog" :title="$vuetify.lang.t('$vuetify.InsertTable')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm0 6h16m-8-8v16"/></svg>
                </button>
				<button
					v-if="isTableActive"
					@click.prevent="showTableBorderMenuToolbar"
					:title="$vuetify.lang.t('$vuetify.TableBorders')"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20V4m4 0v.01M12 4v.01M16 4v.01M20 4v.01M20 8v.01M20 12v.01M20 16v.01M8 20v.01m4-.01v.01m4-.01v.01m4-.01v.01M9 12h6m-3-3v6"/></svg>
				</button>
            </div>
        </div>

        <div
            ref="editor"
            contenteditable="true"
            class="editor-content"
            @input="onInput"
            @click="handleEditorClick"
            :style="editorStyle"
        ></div>

        <div v-if="showLinkDialog" class="dialog-backdrop">
            <div class="dialog">
                <h3>{{ $vuetify.lang.t('$vuetify.EnterHyperlink') }}</h3>
                <input
                    ref="linkInput"
                    v-model="linkUrl"
                    placeholder="https://example.com"
                />
                <div class="dialog-actions">
                    <button @click="insertLink">{{ $vuetify.lang.t('$vuetify.add') }}</button>
                    <button @click="showLinkDialog = false">{{ $vuetify.lang.t('$vuetify.cancel') }}</button>
                </div>
            </div>
        </div>

        <!-- Table Dialog -->
        <div v-if="showTableDialogBox" class="dialog-backdrop">
            <div class="dialog">
                <h3>{{ $vuetify.lang.t('$vuetify.InsertTable') }}</h3>
                <div class="table-controls">
                    <div class="table-control">
                        <label>{{ $vuetify.lang.t('$vuetify.rows') }}:</label>
                        <input
                            type="number"
                            v-model.number="tableRows"
                            min="1"
                            max="10"
                            class="table-input"
                        />
                    </div>
                    <div class="table-control">
                        <label>{{ $vuetify.lang.t('$vuetify.columns') }}:</label>
                        <input
                            type="number"
                            v-model.number="tableCols"
                            min="1"
                            max="10"
                            class="table-input"
                        />
                    </div>
                </div>
                <div class="dialog-actions">
                    <button @click="insertTable">{{ $vuetify.lang.t('$vuetify.add') }}</button>
                    <button @click="showTableDialogBox = false">{{ $vuetify.lang.t('$vuetify.cancel') }}</button>
                </div>
                <div v-if="tableRows < 1 || tableCols < 1" class="dialog-error">
                    {{ $vuetify.lang.t('$vuetify.MiValueError') }}
                </div>
            </div>
        </div>

        <input
            v-if="!disableImageUpload"
            type="file"
            ref="imageInput"
            accept="image/*"
            hidden
            @change="handleImageUpload"
        />
		<div
    v-if="showTableBorderMenu"
    class="table-border-menu"
    :style="{ left: tableBorderMenuPosition.x + 'px', top: tableBorderMenuPosition.y + 'px' }"
>
    <div class="menu-title">{{ $vuetify.lang.t('$vuetify.TableBorders') }}</div>
    <div
        v-for="option in tableBorderOptions"
        :key="option.value"
        class="menu-item"
        @click="applyTableBorder(option.value)"
    >
        {{ option.text }}
    </div>
</div>
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: String,
            default: "",
        },
        disableImageUpload: {
            type: Boolean,
            default: false,
        },
        disableMediaUpload: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedFont: "Arial",
            selectedColor: "#000000",
            showLinkDialog: false,
            savedRange: null,
            linkUrl: "",
            history: [],
            historyIndex: -1,
            showMobileMenu: false,
            isMobile: window.innerWidth < 1080,
            showTableButtons: false,
            showTableDialogBox: false,
            tableRows: 3, // Default value
            tableCols: 3, // Default value
            tableBorders: true,
			tableBorderOptions: [
				{ text: "Все границы", value: "all" },
				{ text: "Нет границ", value: "none" },
				{ text: "Внутренние границы", value: "inner" },
				{ text: "Внешние границы", value: "outer" }
			],
			isTableActive: false,
			showTableBorderMenu: false,
			tableBorderMenuPosition: { x: 0, y: 0 },
			selectedTable: null,
            editorStyle: {
                minHeight: "400px",
                padding: "15px",
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                lineHeight: "1.6",
                border: "none",
                outline: "none",
                color: "black",
            },
        };
    },
    watch: {
        value(newVal) {
            if (newVal !== this.$refs.editor.innerHTML) {
                this.$refs.editor.innerHTML = newVal;
                this.$nextTick(() => {
                    this.reattachTableListeners();
                });
            }
        },
    },
    mounted() {
        this.$refs.editor.innerHTML = this.value;
        this.saveState();
        // After setting the initial content, reattach table listeners
        this.$nextTick(() => {
            this.reattachTableListeners();
        });
        this.$refs.editor.addEventListener("mousedown", this.onResizeMouseDown);
        this.$refs.editor.addEventListener(
            "touchstart",
            this.onResizeTouchStart,
            { passive: false }
        );
		
		this.$refs.editor.addEventListener("keyup", this.checkTableActive);
		this.$refs.editor.addEventListener("mouseup", this.checkTableActive);
		this.$refs.editor.addEventListener("focus", this.checkTableActive);
		this.$refs.editor.addEventListener("click", this.checkTableActive);

        // Global event listener to prevent contenteditable on buttons
        this.$refs.editor.addEventListener(
            "focusin",
            (e) => {
                if (
                    e.target.tagName === "BUTTON" ||
                    e.target.classList.contains("row-btn") ||
                    e.target.classList.contains("col-btn") ||
                    e.target.classList.contains("table-delete-btn")
                ) {
                    // Prevent focus and cursor
                    e.preventDefault();
                    e.stopPropagation();
                    // Move focus to editor instead
                    this.$refs.editor.focus();
                    return false;
                }
            },
            true
        );

        // Another level of protection - capture mousedown on buttons
        this.$refs.editor.addEventListener(
            "mousedown",
            (e) => {
                if (
                    e.target.tagName === "BUTTON" ||
                    e.target.classList.contains("row-btn") ||
                    e.target.classList.contains("col-btn") ||
                    e.target.classList.contains("table-delete-btn")
                ) {
                    // Prevent default mousedown behavior
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }
            },
            true
        );

        window.addEventListener("resize", this.checkMobile);
    },
    beforeDestroy() {
        this.$refs.editor.removeEventListener(
            "mousedown",
            this.onResizeMouseDown
        );
        this.$refs.editor.removeEventListener(
            "touchstart",
            this.onResizeTouchStart
        );
        window.removeEventListener("resize", this.checkMobile);
		this.$refs.editor.removeEventListener("keyup", this.checkTableActive);
		this.$refs.editor.removeEventListener("mouseup", this.checkTableActive);
		this.$refs.editor.removeEventListener("focus", this.checkTableActive);
		this.$refs.editor.removeEventListener("click", this.checkTableActive);
    },
	computed: {
    tableBorderMenuStyle() {
        if (this.isMobile) {
            return {
                position: 'fixed',
                top: '0',
                left: '0',
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: '10000',
                pointerEvents: 'none' // Чтобы не блокировать взаимодействие с фоном
            };
        }
        
        return {
            left: this.tableBorderMenuPosition.x + 'px',
            top: this.tableBorderMenuPosition.y + 'px'
        };
    }
},
    methods: {
showTableBorderMenuToolbar(event) {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    let node = selection.anchorNode;
    let table = null;
    while (node) {
        if (node.nodeType === 1 && node.tagName === "TABLE" && node.classList.contains("editor-table")) {
            table = node;
            break;
        }
        node = node.parentNode;
    }
    if (!table) return;

    this.selectedTable = table;

    if (this.isMobile) {
        // Для мобильных устройств используем фиксированные значения
        this.tableBorderMenuPosition = {
            x: 0, // Будет перезаписано в стиле на 50%
            y: 0  // Будет перезаписано в стиле на 50%
        };
    } else {
        const rect = event.target.getBoundingClientRect();
        this.tableBorderMenuPosition = {
            x: rect.left,
            y: rect.bottom + window.scrollY
        };
    }

    this.showTableBorderMenu = true;

    setTimeout(() => {
        document.addEventListener("click", this.hideTableBorderMenu);
    }, 0);
},
checkTableActive() {
        const selection = window.getSelection();
        if (!selection.rangeCount) {
            this.isTableActive = false;
            return;
        }
        let node = selection.anchorNode;
        while (node) {
            if (node.nodeType === 1 && node.tagName === "TABLE" && node.classList.contains("editor-table")) {
                this.isTableActive = true;
                return;
            }
            node = node.parentNode;
        }
        this.isTableActive = false;
},
showTableBorderOptions(event) {
    const table = event.target.closest("table.editor-table");
    if (table) {
        event.preventDefault();
        this.selectedTable = table;
        this.tableBorderMenuPosition = {
            x: event.clientX,
            y: event.clientY,
        };
        this.showTableBorderMenu = true;

        setTimeout(() => {
            document.addEventListener("click", this.hideTableBorderMenu);
        }, 0);
    }
},

hideTableBorderMenu() {
    this.showTableBorderMenu = false;
    document.removeEventListener("click", this.hideTableBorderMenu);
},

applyTableBorder(borderType) {
    if (!this.selectedTable) return;
    
    this.selectedTable.classList.remove("table-borders-all", "table-borders-none", "table-borders-inner", "table-borders-outer");
    
    switch (borderType) {
        case "all":
            this.selectedTable.classList.add("table-borders-all");
            break;
        case "none":
            this.selectedTable.classList.add("table-borders-none");
            break;
        case "inner":
            this.selectedTable.classList.add("table-borders-inner");
            break;
        case "outer":
            this.selectedTable.classList.add("table-borders-outer");
            break;
    }
    
    this.hideTableBorderMenu();
    this.saveState();
    this.$emit("input", this.$refs.editor.innerHTML);
    this.checkTableActive();
},

handleContextMenu(event) {
    // Проверяем, находимся ли мы на редактируемом маршруте
    if (
        this.$route.path !== "/newsAdd" &&
        !this.$route.path.startsWith("/newsEdit/")
    ) {
        return;
    }
    
    // Проверяем, кликнули ли по таблице
    const table = event.target.closest("table.editor-table");
    if (table) {
        this.showTableBorderOptions(event);
    }
},


        // Show table dialog for inserting table
        showTableDialog() {
            // First focus the editor to ensure the cursor is placed inside it
            this.focusEditor();

            // Set default values if needed
            if (!this.tableRows || this.tableRows < 1) {
                this.tableRows = 3;
            }
            if (!this.tableCols || this.tableCols < 1) {
                this.tableCols = 3;
            }
            this.tableBorders = true;
            this.showTableDialogBox = true;
        },

        // Insert table into editor
        insertTable() {
            this.showTableButtons = true;
            const rows = parseInt(this.tableRows) || 0;
            const cols = parseInt(this.tableCols) || 0;

            console.log("Creating table with rows:", rows, "cols:", cols);

            // Validate input
            if (rows < 1 || cols < 1) {
                alert(
                    "Пожалуйста, укажите количество строк и столбцов (минимум 1)"
                );
                return;
            }

            // Get the current selection
            const selection = window.getSelection();
            const editorNode = this.$refs.editor;
            let range;

            // Check if we have a valid selection within the editor
            let isSelectionInEditor = false;
            if (selection.rangeCount) {
                const currentRange = selection.getRangeAt(0);
                let currentNode = currentRange.commonAncestorContainer;

                // Check if the selection is within the editor
                while (currentNode) {
                    if (currentNode === editorNode) {
                        isSelectionInEditor = true;
                        range = currentRange;
                        break;
                    }
                    currentNode = currentNode.parentNode;
                }
            }

            // If there's no valid selection in the editor, create one at the end
            if (!isSelectionInEditor) {
                this.focusEditor();

                // Create a new range at the end of the editor
                range = document.createRange();

                // Append at the end of the editor
                if (editorNode.childNodes.length > 0) {
                    // If editor has content, put it after the last child
                    const lastChild = editorNode.lastChild;
                    range.setStartAfter(lastChild);
                    range.setEndAfter(lastChild);
                } else {
                    // If editor is empty, put it inside at beginning
                    range.setStart(editorNode, 0);
                    range.setEnd(editorNode, 0);
                }

                // Clear existing selection and set our new range
                selection.removeAllRanges();
                selection.addRange(range);
            }

            // Create container div with table
            const tableContainer = document.createElement("div");
            tableContainer.className = "table-container";

            // Create table element with required rows and columns
            const table = document.createElement("table");
            table.className = "editor-table";
            if (!this.tableBorders) {
                table.classList.add("no-borders");
            }

            // Create column control row
            const colControlsRow = document.createElement("tr");
            colControlsRow.className =
                "column-controls-row editor-controls-only";

            // Add empty cell for corner
            const cornerCell = document.createElement("th");
            cornerCell.className = "corner-cell editor-controls-only";
			cornerCell.contentEditable = false;
			cornerCell.setAttribute("contenteditable", "false");

            // Create a non-editable wrapper for the delete table button
            const buttonWrapper = document.createElement("span");
            buttonWrapper.contentEditable = false;
            buttonWrapper.setAttribute("contenteditable", "false");
            buttonWrapper.className =
                "table-controls-wrapper editor-controls-only";

            // Create delete table button
            const deleteTableBtn = document.createElement("button");
            deleteTableBtn.type = "button"; // Important to specify it's a button
            deleteTableBtn.className =
                "table-delete-btn table-button editor-controls-only";
            deleteTableBtn.textContent = "×";
            deleteTableBtn.title = "Удалить таблицу";
            // Use a more aggressive event prevention approach
            deleteTableBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.deleteTable(e);
                return false;
            });

            // Add the button to the wrapper
            buttonWrapper.appendChild(deleteTableBtn);
            // Add the wrapper to the corner cell (not the button directly)
            cornerCell.appendChild(buttonWrapper);
            colControlsRow.appendChild(cornerCell);

            // Create column control cells
            for (let j = 0; j < cols; j++) {
                const colHeader = document.createElement("th");
				colHeader.contentEditable = false;
				colHeader.setAttribute("contenteditable", "false");

                // Create wrapper for column buttons
                const colButtonsWrapper = document.createElement("span");
                colButtonsWrapper.contentEditable = false;
                colButtonsWrapper.setAttribute("contenteditable", "false");
                colButtonsWrapper.className =
                    "table-controls-wrapper editor-controls-only";

                // Add column button
                const addColBtn = document.createElement("button");
                addColBtn.type = "button";
                addColBtn.className =
                    "col-btn table-button editor-controls-only";
                addColBtn.textContent = "+";
                addColBtn.title = "Добавить колонку";
                addColBtn.setAttribute("data-col-idx", j);
                addColBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.addColumn(e);
                });

                // Delete column button
                const delColBtn = document.createElement("button");
                delColBtn.type = "button";
                delColBtn.className =
                    "col-btn col-delete table-button editor-controls-only";
                delColBtn.textContent = "−";
                delColBtn.title = "Удалить колонку";
                delColBtn.setAttribute("data-col-idx", j);
                delColBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.deleteColumn(e);
                });

                // Add buttons to wrapper
                colButtonsWrapper.appendChild(addColBtn);
                colButtonsWrapper.appendChild(delColBtn);

                // Add wrapper to header cell
                colHeader.appendChild(colButtonsWrapper);
                colControlsRow.appendChild(colHeader);
            }

            // Add column controls to table
            table.appendChild(colControlsRow);

            // Create table rows and cells
            for (let i = 0; i < rows; i++) {
                const row = document.createElement("tr");

                // Create row control cell
                const rowControl = document.createElement("td");
                rowControl.className = "row-controls editor-controls-only";
				rowControl.contentEditable = false;
				rowControl.setAttribute("contenteditable", "false");

                // Create wrapper for row buttons
                const rowButtonsWrapper = document.createElement("span");
                rowButtonsWrapper.contentEditable = false;
                rowButtonsWrapper.setAttribute("contenteditable", "false");
                rowButtonsWrapper.className =
                    "table-controls-wrapper editor-controls-only";

                // Add row button
                const addRowBtn = document.createElement("button");
                addRowBtn.type = "button";
                addRowBtn.className =
                    "row-btn table-button editor-controls-only";
                addRowBtn.textContent = "+";
                addRowBtn.title = "Добавить ряд";
                addRowBtn.setAttribute("data-row-idx", i);
                // Use a more aggressive event prevention approach
                addRowBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.addRow(e);
                });

                // Delete row button
                const delRowBtn = document.createElement("button");
                delRowBtn.className = "row-btn row-delete editor-controls-only";
                delRowBtn.textContent = "−";
                delRowBtn.title = "Удалить ряд";
                delRowBtn.contentEditable = false;
                delRowBtn.setAttribute("data-row-idx", i);
                delRowBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.deleteRow(e);
                });

                rowControl.appendChild(addRowBtn);
                rowControl.appendChild(delRowBtn);
                row.appendChild(rowControl);

                for (let j = 0; j < cols; j++) {
                    const cell = document.createElement("td");
                    cell.className = "editor-table-cell";
                    cell.contentEditable = true;

                    // Set border classes based on settings
                    if (!this.tableBorders) {
                        cell.classList.add("no-border");
                    }

                    cell.innerHTML = "&nbsp;"; // Add space to make cell clickable
                    row.appendChild(cell);
                }

                table.appendChild(row);
            }

            tableContainer.appendChild(table);
			table.className = "editor-table table-borders-all";
            // Insert the table
            range.deleteContents();
            range.insertNode(tableContainer);

            this.focusEditor();
            this.saveState();
            this.$emit("input", this.$refs.editor.innerHTML);
            this.showTableDialogBox = false;
        },

        // Delete table
        deleteTable(event) {
            event.preventDefault();
            const tableElement = event.target.closest("table");
            if (tableElement) {
                const containerElement =
                    tableElement.closest(".table-container");
                if (containerElement) {
                    containerElement.remove();
                    this.saveState();
                    this.$emit("input", this.$refs.editor.innerHTML);
                }
            }
        },

        // Add row to table
        addRow(event) {
            event.preventDefault();
            const rowIdx = parseInt(event.target.getAttribute("data-row-idx"));
            const table = event.target.closest("table");
            if (!table) return;

            const newRowIdx = rowIdx + 1;
            const colCount = table.rows[0].cells.length - 1; // Minus the row controls

            // Create new row
            const newRow = document.createElement("tr");

            // Create row control cell
            const rowControl = document.createElement("td");
            rowControl.className = "row-controls editor-controls-only";
			rowControl.contentEditable = false;
			rowControl.setAttribute("contenteditable", "false");

            // Add row button
            const addRowBtn = document.createElement("button");
            addRowBtn.className = "row-btn editor-controls-only";
            addRowBtn.textContent = "+";
            addRowBtn.title = "Добавить ряд";
            addRowBtn.contentEditable = false;
            addRowBtn.setAttribute("data-row-idx", newRowIdx);
            // Use a more aggressive event prevention approach
            addRowBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.addRow(e);
            });

            // Delete row button
            const delRowBtn = document.createElement("button");
            delRowBtn.className = "row-btn row-delete editor-controls-only";
            delRowBtn.textContent = "−";
            delRowBtn.title = "Удалить ряд";
            delRowBtn.contentEditable = false;
            delRowBtn.setAttribute("data-row-idx", newRowIdx);
            // Use a more aggressive event prevention approach
            delRowBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.deleteRow(e);
            });

            rowControl.appendChild(addRowBtn);
            rowControl.appendChild(delRowBtn);
            newRow.appendChild(rowControl);

            // Create cells
            for (let i = 0; i < colCount; i++) {
                const cell = document.createElement("td");
                cell.className = "editor-table-cell";
                cell.contentEditable = true;

                // Check if table has no-borders class
                if (table.classList.contains("no-borders")) {
                    cell.classList.add("no-border");
                }

                cell.innerHTML = "&nbsp;"; // Add space to make cell clickable
                newRow.appendChild(cell);
            }

            // Insert new row after current row
            const currentRow = table.rows[rowIdx + 1]; // +1 for the column controls row
            table.insertBefore(newRow, currentRow.nextSibling);

            // Update row indexes for all following rows
            for (let i = newRowIdx + 1; i < table.rows.length; i++) {
                const row = table.rows[i];
                if (row.cells[0].classList.contains("row-controls")) {
                    const btns = row.cells[0].querySelectorAll(".row-btn");
                    btns.forEach((btn) => {
                        btn.setAttribute("data-row-idx", i - 1); // -1 for the column controls row
                    });
                }
            }

            this.saveState();
            this.$emit("input", this.$refs.editor.innerHTML);
        },

        // Delete row from table
        deleteRow(event) {
            event.preventDefault();
            const table = event.target.closest("table");
            if (!table || table.rows.length <= 2) return; // Keep at least one row + column headers

            const rowIdx = parseInt(event.target.getAttribute("data-row-idx"));
            const rowToDelete = rowIdx + 1; // +1 for the column controls row

            table.deleteRow(rowToDelete);

            // Update row indexes for all following rows
            for (let i = rowToDelete; i < table.rows.length; i++) {
                const row = table.rows[i];
                if (row.cells[0].classList.contains("row-controls")) {
                    const btns = row.cells[0].querySelectorAll(".row-btn");
                    btns.forEach((btn) => {
                        btn.setAttribute("data-row-idx", i - 1); // -1 for the column controls row
                    });
                }
            }

            this.saveState();
            this.$emit("input", this.$refs.editor.innerHTML);
        },

        // Add column to table
        addColumn(event) {
            event.preventDefault();
            const colIdx = parseInt(event.target.getAttribute("data-col-idx"));
            const table = event.target.closest("table");
            if (!table) return;

            const newColIdx = colIdx + 1;

            // Add header cell in column controls row
            const headerRow = table.rows[0];
            const newHeaderCell = document.createElement("th");
			newHeaderCell.contentEditable = false;
			newHeaderCell.setAttribute("contenteditable", "false");

            // Add column button
            const addColBtn = document.createElement("button");
            addColBtn.className = "col-btn editor-controls-only";
            addColBtn.textContent = "+";
            addColBtn.title = "Добавить колонку";
            addColBtn.contentEditable = false;
            addColBtn.setAttribute("data-col-idx", newColIdx);
            // Use a more aggressive event prevention approach
            addColBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.addColumn(e);
            });

            // Delete column button
            const delColBtn = document.createElement("button");
            delColBtn.className = "col-btn col-delete editor-controls-only";
            delColBtn.textContent = "−";
            delColBtn.title = "Удалить колонку";
            delColBtn.contentEditable = false;
            delColBtn.setAttribute("data-col-idx", newColIdx);
            // Use a more aggressive event prevention approach
            delColBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.deleteColumn(e);
            });

            newHeaderCell.appendChild(addColBtn);
            newHeaderCell.appendChild(delColBtn);

            // Insert new header at colIdx + 1 (to account for the row controls column)
            const insertBeforeHeaderCell = headerRow.cells[colIdx + 2] || null;
            headerRow.insertBefore(newHeaderCell, insertBeforeHeaderCell);

            // Add cell in each data row
            for (let i = 1; i < table.rows.length; i++) {
                const row = table.rows[i];
                const cell = document.createElement("td");
                cell.className = "editor-table-cell";
                cell.contentEditable = true;

                // Check if table has no-borders class
                if (table.classList.contains("no-borders")) {
                    cell.classList.add("no-border");
                }

                cell.innerHTML = "&nbsp;"; // Add space to make cell clickable

                // Insert at colIdx + 1 (to account for the row controls column)
                const insertBeforeCell = row.cells[colIdx + 2] || null;
                row.insertBefore(cell, insertBeforeCell);
            }

            // Update column indexes for all following columns
            const headerCells = headerRow.cells;
            for (let i = newColIdx + 1; i < headerCells.length; i++) {
                const cell = headerCells[i];
                if (cell.querySelector(".col-btn")) {
                    const btns = cell.querySelectorAll(".col-btn");
                    btns.forEach((btn) => {
                        btn.setAttribute("data-col-idx", i - 1);
                    });
                }
            }

            this.saveState();
            this.$emit("input", this.$refs.editor.innerHTML);
        },

        // Delete column from table
        deleteColumn(event) {
            event.preventDefault();
            const table = event.target.closest("table");
            if (!table) return;

            const colIdx = parseInt(event.target.getAttribute("data-col-idx"));
            const colIdxWithOffset = colIdx + 1; // +1 for the row controls column

            // Count all content columns (excluding control column)
            let contentCols = 0;
            for (let i = 0; i < table.rows[0].cells.length; i++) {
                const cell = table.rows[0].cells[i];
                if (!cell.classList.contains("corner-cell")) {
                    contentCols++;
                }
            }

            if (contentCols <= 1) return; // Keep at least one column

            // Delete cell from each row
            for (let i = 0; i < table.rows.length; i++) {
                const row = table.rows[i];
                row.deleteCell(colIdxWithOffset);
            }

            // Update column indexes for all following columns
            const headerRow = table.rows[0];
            const headerCells = headerRow.cells;
            for (let i = colIdxWithOffset; i < headerCells.length; i++) {
                const cell = headerCells[i];
                if (cell.querySelector(".col-btn")) {
                    const btns = cell.querySelectorAll(".col-btn");
                    btns.forEach((btn) => {
                        btn.setAttribute("data-col-idx", i - 1);
                    });
                }
            }

            this.saveState();
            this.$emit("input", this.$refs.editor.innerHTML);
        },

        // Reattach event listeners to table controls when content is loaded
        reattachTableListeners() {
            // Find all tables in the editor
            const tables = this.$refs.editor.querySelectorAll(".editor-table");

            if (!tables || tables.length === 0) return;

            tables.forEach((table) => {
                // Find and reattach delete table button listeners
                const deleteTableBtns =
                    table.querySelectorAll(".table-delete-btn");
                deleteTableBtns.forEach((btn) => {
                    // Make sure the button is not editable
                    btn.contentEditable = false;

                    // Create a new button to replace the old one (removes all event listeners)
                    const newBtn = btn.cloneNode(true);
                    // Add the aggressive event prevention listener
                    newBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.deleteTable(e);
                    });

                    // Replace the old button with the new one
                    btn.parentNode.replaceChild(newBtn, btn);
                });

                // Find and reattach add row button listeners
                const addRowBtns = table.querySelectorAll(
                    ".row-btn:not(.row-delete)"
                );
                addRowBtns.forEach((btn) => {
                    // Make sure the button is not editable
                    btn.contentEditable = false;

                    // Create a new button to replace the old one (removes all event listeners)
                    const newBtn = btn.cloneNode(true);
                    // Add the aggressive event prevention listener
                    newBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.addRow(e);
                    });

                    // Replace the old button with the new one
                    btn.parentNode.replaceChild(newBtn, btn);
                });

                // Find and reattach delete row button listeners
                const delRowBtns = table.querySelectorAll(
                    ".row-btn.row-delete"
                );
                delRowBtns.forEach((btn) => {
                    // Make sure the button is not editable
                    btn.contentEditable = false;

                    // Create a new button to replace the old one (removes all event listeners)
                    const newBtn = btn.cloneNode(true);
                    // Add the aggressive event prevention listener
                    newBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.deleteRow(e);
                    });

                    // Replace the old button with the new one
                    btn.parentNode.replaceChild(newBtn, btn);
                });

                // Find and reattach add column button listeners
                const addColBtns = table.querySelectorAll(
                    ".col-btn:not(.col-delete)"
                );
                addColBtns.forEach((btn) => {
                    // Make sure the button is not editable
                    btn.contentEditable = false;

                    // Create a new button to replace the old one (removes all event listeners)
                    const newBtn = btn.cloneNode(true);
                    // Add the aggressive event prevention listener
                    newBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.addColumn(e);
                    });

                    // Replace the old button with the new one
                    btn.parentNode.replaceChild(newBtn, btn);
                });

                // Find and reattach delete column button listeners
                const delColBtns = table.querySelectorAll(
                    ".col-btn.col-delete"
                );
                delColBtns.forEach((btn) => {
                    // Make sure the button is not editable
                    btn.contentEditable = false;

                    // Create a new button to replace the old one (removes all event listeners)
                    const newBtn = btn.cloneNode(true);
                    // Add the aggressive event prevention listener
                    newBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.deleteColumn(e);
                    });

                    // Replace the old button with the new one
                    btn.parentNode.replaceChild(newBtn, btn);
                });
            });

            console.log("Table event listeners reattached");
        },

        checkMobile() {
            this.isMobile = window.innerWidth < 1080;
        },
        handleMobileMenuClick(event) {
            if (!this.isMobile) return;
            const target = event.target;
            const isFontSelect =
                target.classList.contains("font-select") ||
                target.closest(".font-select");
            const isColorPicker =
                target.classList.contains("color-picker") ||
                target.closest(".color-picker");

            if (!isFontSelect && !isColorPicker) {
                this.showMobileMenu = false;
            }
        },
        focusEditor() {
            this.$refs.editor.focus();
        },
      changeFont() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const initialContent = this.$refs.editor.innerHTML;

        // Сохраняем информацию о том, был ли текст выделен
        const hasSelection = !range.collapsed;

        if (hasSelection) {
          // Применяем шрифт к выделенному тексту (существующий код)
          const container = document.createElement("div");
          const clonedContent = range.cloneContents();
          container.appendChild(clonedContent);

          const applyFontToNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
              const span = document.createElement("span");
              span.style.fontFamily = this.selectedFont;
              span.textContent = node.textContent;
              return span;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
              const clone = node.cloneNode(false);

              clone.style.fontFamily = "";
              clone.style.fontSize = "";
              clone.style.fontWeight = "";
              clone.style.fontStyle = "";

              Array.from(node.childNodes).forEach(child => {
                const processedChild = applyFontToNode(child);
                if (processedChild) {
                  clone.appendChild(processedChild);
                }
              });
              return clone;
            }
            return null;
          };

          const newContent = document.createDocumentFragment();
          Array.from(container.childNodes).forEach(child => {
            const processed = applyFontToNode(child);
            if (processed) {
              newContent.appendChild(processed);
            }
          });

          range.deleteContents();
          range.insertNode(newContent);

          // Устанавливаем курсор в конец вставленного содержимого
          range.setStartAfter(newContent.lastChild);
          range.setEndAfter(newContent.lastChild);
          selection.removeAllRanges();
          selection.addRange(range);
        }

        // Устанавливаем шрифт для нового текста с помощью execCommand
        document.execCommand("styleWithCSS", false, true);
        document.execCommand("fontName", false, this.selectedFont);

        this.focusEditor();
        this.saveState();

        if (this.$refs.editor.innerHTML !== initialContent) {
          this.$emit('input', this.$refs.editor.innerHTML);
        }
      },

        changeTextColor() {
            document.execCommand("foreColor", false, this.selectedColor);
            this.focusEditor();
        },
        resetFormat() {
            document.execCommand("formatBlock", false, "p");
            document.execCommand("removeFormat");
            this.focusEditor();
            this.saveState();
        },
        saveState() {
            const html = this.$refs.editor.innerHTML;
            this.history = this.history.slice(0, this.historyIndex + 1);
            this.history.push(html);
            this.historyIndex++;

            if (this.history.length > 100) {
                this.history.shift();
                this.historyIndex--;
            }
        },
        undo() {
            if (this.historyIndex > 0) {
                this.historyIndex--;
                this.$refs.editor.innerHTML = this.history[this.historyIndex];
                this.$emit("input", this.history[this.historyIndex]);
            }
        },
        redo() {
            if (this.historyIndex < this.history.length - 1) {
                this.historyIndex++;
                this.$refs.editor.innerHTML = this.history[this.historyIndex];
                this.$emit("input", this.history[this.historyIndex]);
            }
        },
        onInput(event) {
            this.saveState();
            this.$emit("input", event.target.innerHTML);
        },
        bold() {
            document.execCommand("bold");
            this.focusEditor();
        },
        italic() {
            document.execCommand("italic");
            this.focusEditor();
        },
        insertOrderedList() {
            document.execCommand("insertOrderedList");
            this.focusEditor();
        },
        insertUnorderedList() {
            document.execCommand("insertUnorderedList");
            this.focusEditor();
        },
        alignLeft() {
            document.execCommand("justifyLeft");
            this.focusEditor();
        },
        alignCenter() {
            document.execCommand("justifyCenter");
            this.focusEditor();
        },
        alignRight() {
            document.execCommand("justifyRight");
            this.focusEditor();
        },
        createLink() {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) {
                alert("Не выделен текст для создания гиперссылки");
                return;
            }

            this.savedRange = selection.getRangeAt(0).cloneRange();
            this.linkUrl = "";
            this.showLinkDialog = true;

            this.$nextTick(() => {
                this.$refs.linkInput?.focus();
            });
        },
        handleEditorClick(event) {
            const link = event.target.closest("a");
            if (link) {
                event.preventDefault();
                window.open(link.href, "_blank");
            }
        },
        insertLink() {
            if (this.linkUrl && this.savedRange) {
                const url = this.linkUrl.startsWith("http")
                    ? this.linkUrl
                    : `http://${this.linkUrl}`;

                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.textContent = this.savedRange.toString();

                this.savedRange.deleteContents();
                this.savedRange.insertNode(link);

                this.$emit("input", this.$refs.editor.innerHTML);

                this.$nextTick(() => {
                    this.saveState();
                    this.focusEditor();
                });
            }
            this.showLinkDialog = false;
            this.savedRange = null;
        },

        insertImage() {
            this.$refs.imageInput.click();
        },

        setHeading(level) {
            document.execCommand("formatBlock", false, `<${level}>`);
            this.focusEditor();
            this.saveState();
        },

        handleImageUpload(event) {
            if (this.disableImageUpload) return;
            const file = event.target.files[0];
            if (!file) return;

            if (!file.type.startsWith("image/")) {
                alert("Пожалуйста, выберите файл изображения");
                event.target.value = "";
                return;
            }

            const formData = new FormData();
            formData.append("file", file);

            this.$axios
                .post(
                    `${this.$hostname}time-tracking/images/upload/news`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                )
                .then((response) => {
                    const serverPath = response.data.data;

                    if (!serverPath) {
                        throw new Error(
                            "Отсутствует путь к файлу в ответе сервера"
                        );
                    }

                    const imageUrl = `${this.$media_url}/${serverPath}`.replace(
                        /([^:]\/)\/+/g,
                        "$1"
                    );

                    return new Promise((resolve, reject) => {
                        const img = new Image();
                        img.onload = () => resolve({ img, imageUrl });
                        img.onerror = () =>
                            reject(new Error("Изображение недоступно"));
                        img.src = imageUrl;
                    });
                })
                .then(({ img, imageUrl }) => {
                    console.log(imageUrl);
                    const initialWidth = Math.min(
                        img.width,
                        this.$refs.editor.offsetWidth * 0.8
                    );

                    const imgHTML = `
                    <div class="resizable-image-container" contenteditable="false">
                        <img src="${imageUrl}" 
                            class="resizable-image" 
                            alt="${file.name}"
                            style="width: ${initialWidth}px; height: auto;">
                        <div class="resize-handle bottom-right"></div>
                    </div>
                    <p><br></p>
                    `;

                    const selection = window.getSelection();
                    if (selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);

                        // Проверяем, находится ли диапазон внутри редактора
                        const isInsideEditor =
                            range.commonAncestorContainer.contains(
                                this.$refs.editor
                            ) ||
                            this.$refs.editor.contains(
                                range.commonAncestorContainer
                            );

                        if (isInsideEditor) {
                            console.log("выделение находится в редакторе");
                            range.deleteContents(); // Удаляем выделенный текст
                            range.insertNode(
                                document
                                    .createRange()
                                    .createContextualFragment(imgHTML)
                            ); // Вставляем новый контент
                        } else {
                            console.log("вставка в конец редактора");
                            this.$refs.editor.insertAdjacentHTML(
                                "beforeend",
                                imgHTML
                            ); // Вставляем в конец редактора
                        }
                    } else {
                        console.log("курсор не выделен");

                        // Получаем позицию курсора
                        const cursorPosition = selection.anchorOffset;
                        const editorContent = this.$refs.editor;

                        // Если курсор установлен внутри редактора
                        if (
                            cursorPosition >= 0 &&
                            cursorPosition <= editorContent.textContent.length
                        ) {
                            console.log("вставка на место курсора");
                            const range = document.createRange();
                            range.setStart(editorContent, cursorPosition); // Устанавливаем начало диапазона на позицию курсора
                            range.collapse(true); // Сворачиваем диапазон в точку
                            range.insertNode(
                                document
                                    .createRange()
                                    .createContextualFragment(imgHTML)
                            ); // Вставляем контент
                        } else {
                            console.log("вставка в конец редактора");
                            editorContent.insertAdjacentHTML(
                                "beforeend",
                                imgHTML
                            ); // Вставляем в конец редактора
                        }
                    }
                    const event = new Event("input", {
                        bubbles: true,
                        cancelable: true,
                    });
                    this.$refs.editor.dispatchEvent(event);
                })
                .catch((error) => {
                    console.error("Ошибка:", error);
                    alert(error.message || "Ошибка загрузки изображения");
                })
                .finally(() => {
                    event.target.value = "";
                });
        },

        onResizeMouseDown(e) {
            const handle = e.target.closest(".resize-handle");
            if (!handle) return;

            e.preventDefault();
            const container = handle.parentElement;
            const img = container.querySelector("img");
            const startX = e.clientX;
            const startWidth = img.offsetWidth;
            const containerWidth = this.$refs.editor.offsetWidth;

            const onMouseMove = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                let newWidth = startWidth + dx;

                newWidth = Math.max(100, Math.min(newWidth, containerWidth));

                img.style.width = `${newWidth}px`;
                img.style.height = "auto";
            };

            const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
                this.saveState();
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        },

        onResizeTouchStart(e) {
            const handle = e.target.closest(".resize-handle");
            if (!handle) return;

            e.preventDefault();
            const container = handle.parentElement;
            const img = container.querySelector("img");
            const startX = e.touches[0].clientX;
            const startWidth = img.offsetWidth;
            const containerWidth = this.$refs.editor.offsetWidth;

            const onTouchMove = (moveEvent) => {
                const dx = moveEvent.touches[0].clientX - startX;
                let newWidth = startWidth + dx;

                // Ограничения размера
                newWidth = Math.max(100, Math.min(newWidth, containerWidth));

                img.style.width = `${newWidth}px`;
                img.style.height = "auto";
            };

            const onTouchEnd = () => {
                document.removeEventListener("touchmove", onTouchMove);
                document.removeEventListener("touchend", onTouchEnd);
                this.saveState();
            };

            document.addEventListener("touchmove", onTouchMove, {
                passive: false,
            });
            document.addEventListener("touchend", onTouchEnd);
        },
    },
};
</script>

<style>
.text-editor {
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    overflow: auto;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: white;
    margin: 20px auto;
}

.toolbar {
    padding: 8px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    display: inline-flex;
    align-items: start;
    flex-wrap: wrap;
    gap: 6px;
}

.toolbar button {
    padding: 6px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    min-width: 36px;
    justify-content: center;
}

.toolbar button:hover {
    background: #f0f0f0;
    border-color: #c0c0c0;
}

.toolbar button:active {
    background: #e0e0e0;
}

.icon {
    width: 20px;
    height: 20px;
    stroke-width: 2;
    stroke: currentColor;
    fill: none;
}

.font-select {
    padding: 6px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    height: 36px;
    font-size: 14px;
}

.color-picker {
    padding: 0;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    width: 36px;
    height: 36px;
    overflow: hidden;
}

.color-picker::-webkit-color-swatch-wrapper {
    padding: 0;
}

.color-picker::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
}

.editor-content {
    min-height: 250px;
    padding: 15px;
}

.editor-content:focus {
    outline: none;
}

.editor-content ol {
    padding-left: 60px !important;
    margin: 10px 0;
    list-style-type: decimal;
}

.editor-content ul {
    padding-left: 60px !important;
    margin: 10px 0;
    list-style-type: disc;
}

.editor-content li {
    margin-left: 20px;
    line-height: 1.6;
}

.dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
}

.dialog {
    background: #fff;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    max-width: 400px;
    width: 100%;
    font-family: Arial, sans-serif;
}

.dialog h3 {
    margin: 0 0 10px;
    font-size: 18px;
}

.dialog input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 8px;
    margin-bottom: 15px;
    font-size: 14px;
}

.dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dialog-actions button {
    padding: 8px 14px;
    font-size: 14px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: #007bff;
    color: white;
    transition: background 0.2s;
}

.dialog-actions button:last-child {
    background: #6c757d;
}

.resizable-image-container {
    position: relative;
    display: inline-block;
    margin: 10px 0;
    max-width: 100%;
}

.resizable-image {
    display: block;
    max-width: 100%;
    height: auto;
    border: 1px solid transparent;
}

.resizable-image-container:hover .resizable-image {
    border-color: #ddd;
}

.resize-handle {
    position: absolute;
    width: 16px !important;
    height: 16px !important;
    background: #4285f4;
    border: 2px solid #fff;
    border-radius: 50%;
    z-index: 2;
    opacity: 1;
    transition: opacity 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.resize-handle.bottom-right {
    bottom: -6px;
    right: -6px;
    cursor: nwse-resize;
}

.editor-content h1 {
    font-size: 2em !important;
    margin: 0.67em 0 !important;
    font-weight: bold !important;
}

.editor-content h2 {
    font-size: 1.5em !important;
    margin: 0.75em 0 !important;
    font-weight: bold !important;
}

.editor-content h3 {
    font-size: 1.17em !important;
    margin: 0.83em 0 !important;
    font-weight: bold !important;
}
.toolbar {
    padding: 8px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: start;
    gap: 8px;
    flex-wrap: nowrap;
}

/* Скрываем мобильное меню на десктопе */
@media (min-width: 1024px) {
    .mobile-menu-toggle {
        display: none !important;
    }

    .toolbar-items {
        display: flex !important;
        flex-wrap: wrap;
        position: static !important;
        background: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
        gap: 8px;
    }
}

/* Базовые стили кнопок */
.toolbar button {
    width: 36px;
    height: 36px;
    padding: 6px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
}

/* Специфичные стили для элементов */
.font-select {
    height: 36px;
    width: 140px;
    padding: 0 8px;
    border-radius: 6px;
}

.color-picker {
    width: 36px;
    height: 36px;
    padding: 3px;
}

@media (max-width: 1080px) {
    .toolbar {
        justify-content: space-between;
        padding: 8px;
        position: relative;
    }

    .left-buttons {
        display: flex;
        gap: 6px;
        order: -1;
    }

    .mobile-menu-toggle {
        order: 1;
        margin-left: auto;
    }

    .toolbar-items {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        z-index: 100;
        padding: 8px;
        display: grid !important;
        grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
        gap: 6px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-top: 1px solid #e0e0e0;
    }

    .toolbar-items button {
        width: 100%;
        min-width: 60px;
    }

    .font-select {
        grid-column: span 2;
    }
}
.toolbar-items.hidden {
    display: none !important;
}

.resize-handle {
    display: none;
}

#switcher .resize-handle,
#switcher ~ .resize-handle {
    display: block !important;
}

.border-row label {
    margin-left: 8px;
    font-size: 14px;
}

/* Table styles */
.editor-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;
    table-layout: fixed;
}

.editor-table td,
.editor-table th {
    border: 1px solid #ccc;
    padding: 8px;
    vertical-align: top;
    position: relative;
    min-width: 30px;
    min-height: 20px;
}

.editor-table.no-borders td,
.editor-table.no-borders th,
.editor-table td.no-border {
    border: 1px dashed #e1ebf8 !important;
}

.editor-table-cell:focus {
    outline: 2px solid #4285f4;
    outline-offset: -2px;
}

.table-container {
    position: relative;
    margin: 1.5rem 0;
}

.table-controls {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
}

.table-control {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.table-input {
    width: 60px;
    border-radius: 4px;
    border: 1px solid #ccc;
    padding: 4px 8px;
}

.row-controls,
.column-controls-row th {
    background-color: #f8f9fa;
    text-align: center;
    padding: 4px;
    user-select: none;
}

.corner-cell {
    width: 40px;
}

.row-btn,
.col-btn,
.table-delete-btn {
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
    padding: 2px 5px;
    margin: 0 2px;
    transition: all 0.2s;
}

.row-btn:hover,
.col-btn:hover,
.table-delete-btn:hover {
    background: #e0e0e0;
}

/* Stronger prevention of content editing in buttons */
button.row-btn,
button.col-btn,
button.table-delete-btn {
    cursor: pointer !important;
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    pointer-events: auto !important;
}

button.row-btn *,
button.col-btn *,
button.table-delete-btn * {
    pointer-events: none !important;
    user-select: none !important;
}

.row-delete,
.col-delete,
.table-delete-btn {
    color: #dc3545;
    font-weight: bold;
}

.border-controls {
    margin-top: 15px;
    margin-bottom: 15px;
}

.dialog-error {
    color: #dc3545;
    font-size: 14px;
    margin-top: 10px;
    text-align: center;
}

/* Cell Context Menu */
.cell-context-menu {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    padding: 8px;
}

.cell-context-menu button {
    background: transparent;
    border: none;
    padding: 8px 12px;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s;
}

.cell-context-menu button:hover {
    background: #f0f0f0;
}

.view-mode-table .editor-controls-only {
    display: none !important;
}

.view-mode-table {
    cursor: default !important;
    outline: none !important;
}

.view-mode-table table,
.view-mode-table td,
.view-mode-table th {
    user-select: text;
    pointer-events: auto !important;
    cursor: text !important;
    -webkit-user-modify: read-only !important;
    -moz-user-modify: read-only !important;
}

/* Override any contentEditable elements */
.view-mode-table [contenteditable="true"],
.view-mode-table [contenteditable="true"] {
    pointer-events: none !important;
    outline: none !important;
    -webkit-user-modify: read-only !important;
    -moz-user-modify: read-only !important;
}

/* Cell border context menu */
.cell-context-menu {
    position: fixed;
    z-index: 1000;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 5px 0;
    min-width: 150px;
}

.context-menu-item {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
}

.context-menu-item:hover {
    background-color: #f0f0f0;
}

/* Individual cell border styles */
.editor-table td.no-border {
    border: 1px dashed #e1ebf8 !important;
}

.dialog-error {
    color: #dc3545;
    font-size: 14px;
    margin-top: 10px;
    text-align: center;
}

/* Global style to prevent any buttons from being contenteditable */
.editor-content button,
.row-btn,
.col-btn,
.table-delete-btn {
    pointer-events: auto !important;
    cursor: pointer !important;
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    caret-color: transparent !important; /* Prevent cursor visibility */
}

.editor-content button.table-button {
    -webkit-user-modify: read-only !important;
    -moz-user-modify: read-only !important;
    pointer-events: auto !important;
    cursor: pointer !important;
    user-select: none !important;
    -moz-user-select: none !important;
    -webkit-user-select: none !important;
    -ms-user-select: none !important;
    outline: none !important;
}

.editor-content button.table-button::selection,
.editor-content button.table-button *::selection {
    background: transparent !important;
    color: inherit !important;
}

.editor-content [contenteditable="false"] {
    cursor: default !important;
}

.editor-content .table-controls-wrapper {
    display: inline-block;
    pointer-events: all;
    cursor: pointer;
    user-select: none;
}

.table-border-menu {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    min-width: 180px;
    padding: 5px 0;
}

.menu-title {
    padding: 8px 12px;
    font-weight: bold;
    border-bottom: 1px solid #eee;
    margin-bottom: 5px;
}

.menu-item {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.menu-item:hover {
    background-color: #f0f0f0;
}

.editor-table.table-borders-all td,
.editor-table.table-borders-all th {
    border: 1px solid #ccc;
}

.editor-table.table-borders-none td,
.editor-table.table-borders-none th {
    border: none;
}

.editor-table.table-borders-inner td,
.editor-table.table-borders-inner th {
    border: 1px solid #ccc;
}

.editor-table.table-borders-inner {
    border: none;
}

.editor-table.table-borders-outer td,
.editor-table.table-borders-outer th {
    border: none;
}

.editor-table.table-borders-outer {
    border: 1px solid #ccc;
}

@media (max-width: 768px) {
    .table-border-menu {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    z-index: 10000 !important;
}
    
    .menu-title {
        font-size: 16px;
        padding: 10px 15px;
    }
    
    .menu-item {
        padding: 12px 15px;
        font-size: 15px;
    }
}
/* Сначала убираем все границы */
.editor-table.table-borders-inner {
    border: none;
}

.editor-table.table-borders-inner td,
.editor-table.table-borders-inner th {
    border: none;
}

/* Добавляем только внутренние границы (справа и снизу) */
.editor-table.table-borders-inner td,
.editor-table.table-borders-inner th {
    border-right: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
}

/* Убираем правую границу у ячеек последнего столбца */
.editor-table.table-borders-inner td:last-child,
.editor-table.table-borders-inner th:last-child {
    border-right: none;
}

/* Убираем нижнюю границу у ячеек последней строки */
.editor-table.table-borders-inner tr:last-child td,
.editor-table.table-borders-inner tr:last-child th {
    border-bottom: none;
}

</style>
